/**
 * /{ws}/search?q= — message search with Slack-style filters (core SearchViewModel, logic/searchQuery): the field keeps
 * the query in the URL, results are newest first and open in context (the conversation or thread, scrolled to the
 * message). As you type, filter suggestions (Slack's search chips: from:, in:, with:, has:, is:, dates) open under the
 * field; ↑ ↓ Enter Tab Esc drive them from the field.
 */
import { Profile, SearchViewModel, Shell, type RichTextContext, type SearchResultRow, type SearchTextEdit } from "@bundle/core";
import { Avatar, BlockedMessageRow, Button, ComingSoon, Divider, Popover, Pressable, RichText, ScrollView, SearchSuggestMenu, Spinner, Stack, Text, TextField, useFormat, useNavigation, useOwned, useT, useViewModel, useWorkspace } from "@bundle/ui";
import { useCallback, useEffect, useId, useMemo, useState } from "react";

function deviceTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

export function SearchScreen({ query }: { query: string }) {
  const ws = useWorkspace();
  // One per Search pane (useOwned: StrictMode-safe, disposed after unmount).
  const id = useId();
  const create = useCallback(() => new SearchViewModel(ws.vm, deviceTimeZone()), [ws.vm]);
  const vm = useOwned(`search:${ws.vm.wsId}:${id}`, create);
  return <SearchView vm={vm} query={query} />;
}

function SearchView({ vm, query }: { vm: SearchViewModel; query: string }) {
  const t = useT();
  const nav = useNavigation();
  const ws = useWorkspace();
  useEffect(() => vm.setQuery(query), [vm, query]);
  const s = useViewModel(vm);
  const cache = ws.state.cache;
  const ctx = useMemo<RichTextContext>(
    () => ({ meId: ws.state.meId, members: cache?.members ?? {}, channels: cache?.channels ?? {}, userGroups: cache?.userGroups ?? {} }),
    [ws.state.meId, cache?.members, cache?.channels, cache?.userGroups],
  );
  const setText = (q: string) => nav.replace(Shell.formatUrl({ type: "search", ws: ws.slug, query: q }));
  // A chosen suggestion rewrites the field and puts the caret after it.
  const [selection, setSelection] = useState<{ start: number; end: number; seq: number }>();
  const apply = (edit: SearchTextEdit) => {
    setText(edit.text);
    setSelection((prev) => ({ start: edit.caret, end: edit.caret, seq: (prev?.seq ?? 0) + 1 }));
  };

  return (
    <Stack grow testID="search-screen">
      <Stack gap="2" paddingX="8" paddingY="5">
        <Text variant="title2" weight="bold">
          {t("search.title")}
        </Text>
        <Popover
          open={s.suggest !== undefined}
          onClose={() => vm.dismissSuggest()}
          side="bottom"
          align="start"
          fill
          width="anchor"
          autoFocus={false}
          anchor={
            <TextField
              value={query}
              onChange={setText}
              onSelectionChange={(_start, end) => vm.setCaret(end)}
              onKey={(key) => {
                const r = vm.suggestKey(key);
                if (r.edit !== undefined) apply(r.edit);
                return r.handled;
              }}
              selection={selection}
              placeholder={t("search.placeholder")}
              leadingIcon="search"
              autoFocus
              testID="search-input"
            />
          }
        >
          {s.suggest === undefined ? null : (
            <SearchSuggestMenu
              menu={s.suggest}
              onChoose={(i) => {
                const edit = vm.chooseSuggest(i);
                if (edit !== undefined) apply(edit);
              }}
              onHover={(i) => vm.highlightSuggest(i)}
              onPickDate={(date, month) => {
                const edit = vm.chooseDate(date, month);
                if (edit !== undefined) apply(edit);
              }}
              testID="search-suggest"
            />
          )}
        </Popover>
        <Text variant="footnote" color="text.tertiary" testID="search-hint">
          {t("search.hint")}
        </Text>
        {s.unresolved.length === 0 ? null : (
          <Text variant="footnote" color="text.secondary" testID="search-unresolved">
            {t("search.unresolved", { filters: s.unresolved.join(", ") })}
          </Text>
        )}
      </Stack>
      <Divider />
      {s.status === "idle" ? (
        <ComingSoon icon="search" title={t("search.title")} body={t("search.hint")} />
      ) : s.status === "error" ? (
        <Stack grow align="center" justify="center" gap="3" padding="8" testID="search-error">
          <Text color="text.secondary" align="center">
            {t("search.error")}
          </Text>
          <Button variant="secondary" label={t("common.retry")} onPress={() => vm.retry()} testID="search-retry" />
        </Stack>
      ) : s.status === "loading" && s.results.length === 0 ? (
        <Stack grow align="center" justify="center" testID="search-loading">
          <Spinner />
        </Stack>
      ) : s.results.length === 0 ? (
        <Stack padding="8" align="center" testID="search-empty">
          <Text color="text.secondary" align="center">
            {t("search.empty", { query: s.query.trim() })}
          </Text>
        </Stack>
      ) : (
        <ScrollView>
          <Stack paddingX="5" paddingY="3" gap="1" testID="search-results">
            <Stack paddingX="3">
              <Text variant="footnote" color="text.tertiary">
                {t("search.resultCount", { count: s.results.length })}
              </Text>
            </Stack>
            {s.results.map((row) =>
              row.collapsed === true ? (
                <BlockedMessageRow key={row.id} onShow={() => vm.showBlocked(row.id)} testID={`search-blocked-${row.id}`} />
              ) : (
                <ResultRow key={row.id} row={row} ctx={ctx} onOpen={() => nav.open(row.url)} />
              ),
            )}
          </Stack>
        </ScrollView>
      )}
    </Stack>
  );
}

function ResultRow({ row, ctx, onOpen }: { row: SearchResultRow; ctx: RichTextContext; onOpen: () => void }) {
  const t = useT();
  const fmt = useFormat();
  const member = ctx.members[row.authorId];
  const look = member === undefined ? {} : Profile.avatarLook(member);
  return (
    <Pressable onPress={onOpen} direction="horizontal" radius="md" accessibilityLabel={`${row.authorName}, ${row.conversation}`} testID={`search-result-${row.id}`}>
      <Stack direction="horizontal" align="start" gap="3" paddingX="3" paddingY="2" grow radius="md">
        <Avatar name={row.authorName} src={look.src ?? row.authorAvatarUrl} tone={look.tone} emoji={look.emoji} size="avatar.md" />
        <Stack grow gap="1">
          <Stack direction="horizontal" align="center" gap="2" wrap>
            <Text weight="semibold" lines={1}>
              {row.authorName}
            </Text>
            <Text variant="footnote" color="text.secondary" lines={1}>
              {row.conversation}
            </Text>
            {row.inThread ? (
              <Text variant="footnote" color="text.tertiary">
                {t("search.inThread")}
              </Text>
            ) : null}
            <Text variant="caption" color="text.tertiary">
              {fmt.dateTime(row.message.createdAt)}
            </Text>
          </Stack>
          <RichText blocks={row.message.blocks} ctx={ctx} />
        </Stack>
      </Stack>
    </Pressable>
  );
}
